import { queryCategoryLabel } from "@anthra/platform/domain"
import { Description, PageTitle, SectionTitle } from "@anthra/ui/typography"
import { createFileRoute, Link } from "@tanstack/react-router"

import { useMyActivity } from "@/features/activity/use-activity"
import { useSession } from "@/features/auth/use-auth"
import { PersonalisedServicesBanner } from "@/features/content/personalised-services-banner"
import { HomeActions } from "@/features/home/home-actions"
import {
	ActivityEmpty,
	ActivityError,
	type ActivityItem,
	ActivityList,
	ActivitySkeleton,
} from "@/features/home/recent-activity"
import { useMyQueries } from "@/features/queries/use-queries"
import { useMyVettingRequests } from "@/features/vetting/use-vetting"

/**
 * Home — where sign-in lands when there was no held destination
 * (US-00-001.1/.3/.5).
 *
 * Built against screens/home.html: the greeting, the three actions with their
 * count lines, recent activity across both kinds of work, and the Personalised
 * Services banner (US-00-005.1). Nothing here writes anything.
 *
 * THE COUNTS ARE THE SERVER'S, NOT A TALLY OF THE FIRST PAGE. Each comes from a
 * list call's `pagination.totalCount` with a status filter, so "2 open" is the
 * number of open questions rather than the number that happened to fit in the
 * rows that came back. Five small cached calls, and every endpoint already
 * existed — no API change was needed for this screen.
 */
export const Route = createFileRoute("/_app/home")({
	component: Home,
})

/** Morning / afternoon / evening, as the design greets. */
function greeting(now: Date): string {
	const hour = now.getHours()
	if (hour < 12) return "Good morning"
	if (hour < 17) return "Good afternoon"
	return "Good evening"
}

/**
 * "2 hours ago", "Yesterday", "30 Jul 2026" — the design's own three shapes.
 * Anything inside a day is relative, because that is the window in which a
 * person still thinks in hours; beyond it a date is easier to place.
 */
function updatedLabel(iso: string, now: Date): string {
	const then = new Date(iso)
	const minutes = Math.round((now.getTime() - then.getTime()) / 60000)
	if (Number.isNaN(minutes)) return ""
	if (minutes < 1) return "Just now"
	if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`
	const hours = Math.round(minutes / 60)
	if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`
	if (hours < 48) return "Yesterday"
	return then.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
}

function Home() {
	const session = useSession()
	const now = new Date()
	// The FULL name, not just the first (client feedback).
	const fullName = session.data?.fullName.trim() ?? ""

	// The two lists that fill recent activity, and the filtered ones that only
	// their totalCount is read from.
	const queries = useMyQueries()
	const vetting = useMyVettingRequests()
	const openQueries = useMyQueries({ status: "open" })
	const answeredQueries = useMyQueries({ status: "answered" })
	const inProgressVetting = useMyVettingRequests({ status: "in_progress" })
	const activity = useMyActivity()

	const isLoading = queries.isPending || vetting.isPending
	const isError = queries.isError || vetting.isError

	const items: ActivityItem[] = [
		...(queries.data?.data ?? []).map(query => ({
			kind: "query" as const,
			reference: query.reference,
			title: query.title,
			category: queryCategoryLabel(query.category),
			status: query.status,
			sortKey: query.lastUpdatedAt,
			updatedLabel: updatedLabel(query.lastUpdatedAt, now),
			to: `/queries/${query.id}`,
		})),
		...(vetting.data?.data ?? []).map(request => {
			// A vetting request carries no lastUpdatedAt — its summary has the
			// three moments it actually moves through. The latest of them IS its
			// last update, and reading them in order keeps the mixed list
			// ordered by the same thing on both sides.
			const movedAt = request.closedAt ?? request.reviewedAt ?? request.submittedAt
			return {
				kind: "vetting" as const,
				reference: request.reference,
				title: request.title,
				status: request.status,
				sortKey: movedAt,
				updatedLabel: updatedLabel(movedAt, now),
				to: `/vetting/${request.id}`,
			}
		}),
	]
		// Newest first across BOTH kinds, sorted on the timestamp rather than on
		// the rendered label — "Yesterday" and "2 hours ago" do not compare.
		.sort((a, b) => b.sortKey.localeCompare(a.sortKey))
		.slice(0, 6)

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col gap-1">
				<PageTitle>
					{fullName === "" ? greeting(now) : `${greeting(now)}, ${fullName}`}
				</PageTitle>
			</div>

			{/* THE BAND SITS DIRECTLY UNDER THE GREETING, NOT ABOVE IT.
			    "Top of the page" and "first element in the document" are not the
			    same thing here: the greeting is the h1, and the band carries its
			    own h2, so putting the band first would open the page on an h2 and
			    leave the h1 beneath it. Under the greeting it is still the first
			    thing on the page with any weight, and the outline stays right. */}
			<PersonalisedServicesBanner screen="home" />

			<HomeActions
				counts={{
					queriesTotal: queries.data?.pagination.totalCount,
					queriesOpen: openQueries.data?.pagination.totalCount,
					awaitingRating: answeredQueries.data?.pagination.totalCount,
					vettingTotal: vetting.data?.pagination.totalCount,
					vettingInProgress: inProgressVetting.data?.pagination.totalCount,
					unseenQueries: activity.data?.queries,
					unseenVetting: activity.data?.vetting,
				}}
			/>

			{/* FULL WIDTH NOW THAT THE RAIL IS EMPTY. This was a two-column grid
			    whose second child was the contact block; with that promoted to the
			    band above, keeping the grid would leave Recent activity in a
			    narrow column with 20rem of nothing beside it. */}
			<div className="flex flex-col gap-6">
				<section
					className="rounded-card border-border bg-card shadow-card flex flex-col gap-4 border p-4 sm:p-6"
					aria-labelledby="home-activity"
				>
					<div className="flex flex-col gap-1">
						<SectionTitle id="home-activity">Recent activity</SectionTitle>
						<Description>
							Your latest questions and vetting requests, together.
						</Description>
					</div>

					{isLoading ? <ActivitySkeleton /> : null}
					{!isLoading && isError ? (
						<ActivityError
							onRetry={() => {
								void queries.refetch()
								void vetting.refetch()
							}}
						/>
					) : null}
					{!isLoading && !isError && items.length === 0 ? <ActivityEmpty /> : null}
					{!isLoading && !isError && items.length > 0 ? (
						<ActivityList items={items} />
					) : null}

					{!isLoading && !isError && items.length > 0 ? (
						<div className="border-border flex flex-wrap items-center gap-x-5 gap-y-2 border-t pt-4">
							<Link
								className="text-description text-link hover:text-link-hover focus-visible:ring-ring rounded-control font-medium focus-visible:ring-2 focus-visible:outline-none"
								to="/queries"
							>
								View all questions
							</Link>
							<Link
								className="text-description text-link hover:text-link-hover focus-visible:ring-ring rounded-control font-medium focus-visible:ring-2 focus-visible:outline-none"
								to="/vetting"
							>
								View all vetting requests
							</Link>
						</div>
					) : null}
				</section>
			</div>
		</div>
	)
}
